import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import {
  DictionaryClientDataRequestAddressType,
  DictionaryClientDataRequestType,
} from 'src/app/extensions/constants/dictionaryConstants/dictionaryConstants';
import { AddressAPIService } from 'src/app/extensions/services/api/address-api.service';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { PartnerAPIService } from 'src/app/extensions/services/api/partner-api.service';
import { AddressTypeEnum } from 'src/app/models/ClientDataEnums';
import { FilesHandler } from '../../utils/files-handler';
import { CheckBoxRounded, InputSize } from '@progress/kendo-angular-inputs';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { isNotEmpty } from 'src/app/extensions/utilities';
import { SIGNAL_ITEM_KEY } from 'src/app/extensions/constants/commonConstants';

@Component({
  selector: 'app-add-address-request',
  templateUrl: './add-address-request.component.html',
  styleUrls: ['./add-address-request.component.scss'],
  standalone: false,
})
export class AddAddressInformationRequestComponent implements OnInit {
  @ViewChild('form') public form: NgForm;

  isMailingAddressDisplayed: boolean;
  isSameAddress: boolean = false;

  public size: InputSize = 'large';
  public rounded: CheckBoxRounded = 'large';

  partnerAddress: app.models.GetPartnerAddressModel[] = {} as app.models.GetPartnerAddressModel[];

  clientDataRequestSaveModel: app.models.ClientDataRequestSaveModel = {
    addresses: [],
    documentsAttached: [],
  } as app.models.ClientDataRequestSaveModel;

  isSendButtonDisabled: boolean = true;
  isRequestSaved: boolean;
  isDataLoaded: boolean = false;

  dictionaryClientDataRequestAddressTypes: app.models.DictionaryDataModel[];
  isPJ: boolean;

  private stateSignalItem: any;

  constructor(
    private _globalService: GlobalService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _addressService: AddressAPIService,
    private _dictionaryService: DictionaryAPIService,
    private _partnerService: PartnerAPIService,
    public _filesHandler: FilesHandler
  ) {
    this.stateSignalItem = this._globalService.getSignalItem(SIGNAL_ITEM_KEY.RQST_CLIENT_DATA);

    if (isNotEmpty(this.stateSignalItem)) {
      this.clientDataRequestSaveModel = this.stateSignalItem.data as app.models.ClientDataRequestSaveModel;
      this.isPJ = this.stateSignalItem.extras?.isPJ;
      this.isSendButtonDisabled = this.stateSignalItem.extras?.isSendButtonDisabled;
      this.isDataLoaded = this.stateSignalItem.extras?.isDataLoaded;
    }
  }

  ngOnInit(): void {
    if (!isNotEmpty(this.stateSignalItem)) {
      this.loadData();
    }
  }

  loadData() {
    let source = forkJoin([
      this._addressService.getAddressInfo(),
      this._dictionaryService.getClientDataRequestTypes(),
      this._dictionaryService.getDictionaryClientDataRequestAddressTypes(),
      this._partnerService.getCompanyInfo(),
    ]);

    source.subscribe(
      ([
        getPartnerAddressModel,
        dictionaryClientDataRequestTypes,
        dictionaryClientDataRequestAddressTypes,
        companyInfo,
      ]) => {
        if (companyInfo.data) {
          this.isPJ = companyInfo.data.isPJ;
          this.clientDataRequestSaveModel.partnerExternalEntityId = companyInfo.data.partnerExternalEntityId;
        }

        if (dictionaryClientDataRequestAddressTypes.data) {
          this.dictionaryClientDataRequestAddressTypes = dictionaryClientDataRequestAddressTypes.data;

          if (getPartnerAddressModel.data) {
            let homeAddress = getPartnerAddressModel.data.find(x => x.addressType == AddressTypeEnum.Address);
            let addressRequest = {} as app.models.ClientDataRequestsAddressModel;
            if (homeAddress) {
              addressRequest = {
                country: homeAddress.country,
                city: homeAddress.district,
                state: homeAddress.locality,
                streetName: homeAddress.street,
                streetNumber: homeAddress.streetNo,
                buildingNumber: homeAddress.buildingNumber,
                stair: homeAddress.stairNumber,
                floor: homeAddress.floorNumber,
                flatNumber: homeAddress.flatNumber,
                postalCode: homeAddress.postalCode,
                dictionaryClientDataRequestAddressTypeId: this.dictionaryClientDataRequestAddressTypes.find(
                  x =>
                    x.code ==
                    (this.isPJ
                      ? DictionaryClientDataRequestAddressType.Office
                      : DictionaryClientDataRequestAddressType.Home)
                ).id,
                externalPartnerAddressId: homeAddress.partnerAddressId,
                externalDictionaryPartnerAddressId: homeAddress.dictionaryPartnerAddressId,
              };
            }
            this.clientDataRequestSaveModel.addresses.push(addressRequest);
          }
        }

        if (dictionaryClientDataRequestTypes.data) {
          let addressRequestType = dictionaryClientDataRequestTypes.data.find(
            x => x.code == DictionaryClientDataRequestType.Address
          );
          if (addressRequestType) {
            this.clientDataRequestSaveModel.dictionaryClientDataRequestTypeId = addressRequestType.id;
            this.clientDataRequestSaveModel.description = addressRequestType.description;
            this.isSendButtonDisabled = false;
          }
        }
        this.isDataLoaded = true;
      }
    );
  }

  toggleMailingAddressHandler() {
    this.isMailingAddressDisplayed = !this.isMailingAddressDisplayed;
    this.isSameAddress = false;
    if (this.isMailingAddressDisplayed) {
      let dictionaryClientDataRequestAddressTypeId = this.dictionaryClientDataRequestAddressTypes.find(
        x => x.code == DictionaryClientDataRequestAddressType.Mailing
      ).id;
      this.clientDataRequestSaveModel.addresses.push({
        country: null,
        city: null,
        state: null,
        streetName: null,
        streetNumber: null,
        buildingNumber: null,
        stair: null,
        floor: null,
        flatNumber: null,
        postalCode: null,
        dictionaryClientDataRequestAddressTypeId: dictionaryClientDataRequestAddressTypeId,
        externalPartnerAddressId: undefined,
        externalDictionaryPartnerAddressId: undefined,
      } as app.models.ClientDataRequestsAddressModel);
    } else {
      this.clientDataRequestSaveModel.addresses.pop();
    }
  }

  copyAddressHandler() {
    if (this.isSameAddress) {
      let dictionaryClientDataRequestAddressTypeId = this.dictionaryClientDataRequestAddressTypes.find(
        x => x.code == DictionaryClientDataRequestAddressType.Mailing
      ).id;
      let address = { ...this.clientDataRequestSaveModel.addresses[0] };
      address.externalPartnerAddressId = null;
      address.externalDictionaryPartnerAddressId = null;
      address.dictionaryClientDataRequestAddressTypeId = dictionaryClientDataRequestAddressTypeId;
      this.clientDataRequestSaveModel.addresses[1] = address;
    }
  }

  sendRequestCofirmationHandler() {
    if (this.form.invalid) {
      return;
    }

    this.stateSignalItem = {
      data: this.clientDataRequestSaveModel,
      extras: {
        isPJ: this.isPJ,
        isSendButtonDisabled: this.isSendButtonDisabled,
        isDataLoaded: this.isDataLoaded,
      },
      routePath: this._activatedRoute.snapshot.routeConfig.path,
    };
    this._globalService.setSignalItem(SIGNAL_ITEM_KEY.RQST_CLIENT_DATA, this.stateSignalItem);
    this._router.navigate(['requests/client-data/new/request-preview']);
  }

  redirectFieldSelectionHandler() {
    this._router.navigate(['requests/client-data/new']);
  }
}
